import { Input } from "../../../components/Input";
import { TextArea } from "../../../components/TextArea";
import { Button } from "../../../components/Button";
import { Box } from "../../../components/Box";
import { Flex } from "../../../components/Flex";
import Select, { Option } from "../../../components/Select";
import { Formik, Form } from "formik";
import { H2 } from "../../../components/Heading";
import { Toggle } from "../../../components/Toggle";
import { Label } from "../../../components/Label";
import { FormRow } from "../../../components/Form";
import { useQuestionsFiledTypeList } from "../../../data/useQuestionsFiledTypeList";
import { validateQuestionForm } from "../../../utils/validationSchema";
import { Paragraph } from "../../../components/Paragraph";
import { useEffect, useMemo, useState } from "react";
import { useDocumentCaptureTypeList } from "../../../data/useDocumentCaptureTypeList";
import { Slider } from "../../../components/Slider";

export const FormQuestion = ({ isOpen, onRequestClose, onSave, question }) => {
  const { data: questionsFieldTypeList } = useQuestionsFiledTypeList();

  const handleAddOption = (setFieldValue, options) => {
    setFieldValue("options", [...options, { value: "", label: "", error: "" }]);
  };

  const [selectedOptions, setSelectedOptions] = useState<Option[]>([]);
  const { data: DocumentCaptureTypeList } = useDocumentCaptureTypeList();

  const [initialFileOptions, setInitialFileOptions] = useState<Option[]>([]);

  const handleOptionChange = (setFieldValue, options, index, value) => {
    const updatedOptions = [...options];
    updatedOptions[index] = value;

    setFieldValue("options", updatedOptions);
  };

  const handleRemoveOption = (setFieldValue, options, index) => {
    const updatedOptions = options.filter((_, i) => i !== index);
    setFieldValue("options", updatedOptions);
  };

  const dataTypeOptions = questionsFieldTypeList?.data?.map(
    ({ id, name, slug_name }) => ({
      value: id,
      label: name,
      slug_name: slug_name,
    })
  );

  const FileUploadoptions = useMemo(
    () =>
      DocumentCaptureTypeList?.data.map(({ name, id }) => ({
        label: name,
        value: id,
      })) as Option[],
    [DocumentCaptureTypeList]
  );

  // find the “file_upload” option
  const fileUploadOption = dataTypeOptions?.find(
    (opt) => opt.slug_name === "file_upload"
  );
  const fileUploadTypeId = fileUploadOption?.value;

  useEffect(() => {
    if (isOpen) {
      if (question) {
        const isFileUpload =
          question.field_type?.slug_name === "file_upload" ||
          question.field_type === fileUploadTypeId;

        if (isFileUpload && DocumentCaptureTypeList?.data) {
          const checks = DocumentCaptureTypeList.data.filter(
            (check) =>
              question.meta_data.options.includes(check.id) ||
              question.meta_data.options.includes(String(check.id)) ||
              question.meta_data.options.includes(check.name)
          );

          const preselected = checks.map(({ id, name }) => ({
            label: name,
            value: id,
          }));

          setSelectedOptions(preselected);

          //  set Formik's values.options
          setTimeout(() => {
            setInitialFileOptions(preselected);
          }, 0);
        }
      } else {
        setSelectedOptions([]);
        setInitialFileOptions([]); // reset
      }
    }
  }, [isOpen, DocumentCaptureTypeList, question, fileUploadTypeId]);

  const handleSubmitQuestion = (values, resetForm) => {
    const fieldTypeId =
      typeof values.field_type === "object"
        ? values.field_type.id
        : values.field_type;

    const transformedOptions = values.options.map((option) => {
      if (option.label !== undefined) return option.label;
      if (option.value !== undefined) return option.value;
      return option;
    });

    const updatedValues = {
      ...question,
      ...values,
      field_type: fieldTypeId,
      meta_data:
        transformedOptions.length > 0
          ? {
              options: transformedOptions,
            }
          : null,
    };

    delete updatedValues.options;
    onSave(updatedValues);
    resetForm();
  };

  const handleSelectChange = (
    selectedOption: Option | Option[] | null,
    setFieldValue: (field: string, value: any) => void,
    setFieldTouched: (field: string, touched: boolean) => void
  ) => {
    const selected = Array.isArray(selectedOption)
      ? selectedOption
      : selectedOption
      ? [selectedOption]
      : [];

    setSelectedOptions(selected);

    setFieldValue("options", selected);

    setFieldTouched("options", true);
  };

  return (
    <Slider width="600px" isOpen={isOpen} onRequestClose={onRequestClose} >
       <Box px={4} py={3}  overflowY="auto"  height={"calc(100vh - 211px)"}>
      <Formik
        initialValues={{
          title: question?.title || "",
          description: question?.description || "",
          field_type: question?.field_type?.id || question?.field_type || "",
          required: question?.required || false,
          options: (question?.meta_data?.options || [])
            .map((o) =>
              typeof o === "string" || typeof o === "number"
                ? { value: o, label: String(o) }
                : o
            )
            .map((opt) => {
              // Use mapped file upload options if available
              const match = initialFileOptions.find(
                (f) => f.value === opt.value
              );
              return match || opt;
            }),
        }}
        validationSchema={validateQuestionForm}
        onSubmit={(values, { resetForm }) => {
          handleSubmitQuestion(values, resetForm);
        }}
      >
        {({
          values,
          handleChange,
          setFieldValue,
          handleSubmit,
          setFieldTouched,
          validateForm,
          validateField,
          errors,
          touched,
          dirty,
          isValid,
        }) => (
          <Box>
            <H2>Add Question</H2>
            <Form onSubmit={handleSubmit}>
              <Flex
                my={1}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label mb={"0px"}>Required</Label>
                <Toggle
                  isChecked={values.required}
                  onChange={(e: any) => {
                    setFieldValue("required", e.target.checked);
                  }}
                />
              </Flex>

              <FormRow my={1.5}>
                <Input
                  width={"100%"}
                  label="Title"
                  placeholder="Enter question title"
                  value={values.title}
                  onChange={handleChange}
                  name="title"
                  isRequired
                  hasError={!!errors.title}
                  errorMessage={
                    typeof errors.title === "string" ? errors.title : undefined
                  }
                />
              </FormRow>

              <FormRow my={2}>
                <TextArea
                  width={"100%"}
                  label="Description (optional)"
                  placeholder="Enter question description"
                  value={values.description}
                  onChange={handleChange}
                  name="description"
                  rows={3}
                  hasError={!!errors.description}
                  errorMessage={
                    typeof errors.description === "string"
                      ? errors.description
                      : undefined
                  }
                />
              </FormRow>

              <FormRow my={1.5}>
                <Select
                  width={"100%"}
                  options={dataTypeOptions}
                  onChange={(option: any) => {
                    setFieldValue("field_type", option.value);

                    const needsOptions =
                      option.slug_name === "choice" ||
                      option.slug_name === "multi_select" ||
                      option.slug_name === "file_upload";

                    setFieldValue("options", needsOptions ? [] : []);

                    setFieldTouched("field_type", true);
                    setTimeout(() => {
                      validateField("field_type");
                      if (needsOptions) {
                        validateField("options");
                      } else {
                        // Explicitly clear errors if no validation is needed
                        setFieldTouched("options", false);
                        setFieldValue("options", []);
                      }
                    }, 0);
                  }}
                  value={
                    dataTypeOptions.find(
                      (option) => option.value === values.field_type
                    ) || null
                  }
                  label="Field type"
                  isInModal
                  isRequired
                  name="dataType"
                  hasError={!!errors.field_type}
                  errorMessage={
                    typeof errors.field_type === "string"
                      ? errors.field_type
                      : undefined
                  }
                />
              </FormRow>

              {(() => {
                const selectedOption = dataTypeOptions.find(
                  (option) => option.value === values.field_type
                );

                return (
                  selectedOption?.slug_name === "choice" ||
                  selectedOption?.slug_name === "multi_select"
                );
              })() && (
                <Box mt={4}>
                  <Label>Options</Label>

                  {values.options.map((option, index) => (
                    <Flex key={index} flexDirection={"column"}>
                      <Flex
                        alignItems="center"
                        justifyContent={"space-between"}
                        mt={2}
                        width={"100%"}
                      >
                        <Input
                          width={"90%"}
                          value={
                            typeof option === "object" ? option.label || "" : ""
                          }
                          placeholder={`Option ${index + 1}`}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (value.includes(",")) {
                              setFieldValue(
                                `options[${index}].error`,
                                "Commas are not allowed"
                              );
                              return;
                            }

                            const newOption = {
                              value,
                              label: value,
                              error: "",
                            };

                            handleOptionChange(
                              setFieldValue,
                              values.options,
                              index,
                              newOption
                            );
                          }}
                        />

                        {option && (
                          <Button
                            onClick={() =>
                              handleRemoveOption(
                                setFieldValue,
                                values.options,
                                index
                              )
                            }
                            ml={2}
                          >
                            Remove
                          </Button>
                        )}
                      </Flex>

                      {errors.options?.[index]?.label && (
                        <Box>
                          <Paragraph color="red.primary">
                            Please enter a valid option, cannot be empty.
                          </Paragraph>
                        </Box>
                      )}
                    </Flex>
                  ))}

                  {typeof errors.options === "string" && (
                    <Paragraph color="red.primary" mt={1}>
                      {errors.options}
                    </Paragraph>
                  )}

                  <Button
                    variant="blueText"
                    onClick={() =>
                      handleAddOption(setFieldValue, values.options)
                    }
                    mt={2}
                    p={"0px"}
                    isDisabled={
                      values.options[values.options.length - 1] === ""
                    }
                  >
                    + Add Option
                  </Button>
                </Box>
              )}
              {(() => {
                const selectedOption = dataTypeOptions.find(
                  (option) => option.value === values.field_type
                );

                return selectedOption?.slug_name === "file_upload";
              })() && (
                <Box mt={4}>
                  <Select
                    isMulti
                    isInModal
                    name="options"
                    options={FileUploadoptions}
                    value={selectedOptions}
                    onChange={(e) => {
                      handleSelectChange(e, setFieldValue, setFieldTouched);

                      setTimeout(() => {
                        setFieldTouched("options", true);
                        validateField("options"); // re-validate after field change
                      }, 0);
                    }}
                    placeholder="Select document type"
                    label="File Type"
                    isRequired
                    hasError={touched.options && !!errors.options}
                    errorMessage={
                      typeof errors.options === "string"
                        ? errors.options
                        : undefined
                    }
                  />
                </Box>
              )}

              <Flex justifyContent="end" mt={4}>
                <Button onClick={onRequestClose} variant="secondary" mr={2}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  isDisabled={(() => {
                    const selectedOption = dataTypeOptions.find(
                      (option) => option.value === values.field_type
                    );
                    const isChoiceOrMultiSelect =
                      selectedOption?.slug_name === "choice" ||
                      selectedOption?.slug_name === "multi_select";

                    const isFileUpload =
                      selectedOption?.slug_name === "file_upload";

                    const validOptions =
                      values.options?.filter(
                        (option) =>
                          option &&
                          typeof option === "object" &&
                          String(option.value || "").trim() !== "" &&
                          !option.error
                      ) || [];

                    const areOptionsInvalid =
                      isChoiceOrMultiSelect &&
                      (values.options.some(
                        (option) =>
                          option?.error ||
                          !option?.value ||
                          String(option.value).trim() === ""
                      ) ||
                        validOptions.length < 2);

                    const areFileUploadInvalid =
                      isFileUpload &&
                      (values.options.some((option) => option?.error) ||
                        validOptions.length === 0);

                    return (
                      !dirty ||
                      (dirty && !isValid) ||
                      areOptionsInvalid ||
                      areFileUploadInvalid
                    );
                  })()}
                >
                  {question ? "Update" : "Create"}
                </Button>
              </Flex>
            </Form>
          </Box>
        )}
      </Formik>
      </Box>
    </Slider>
  );
};
